import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiFetch } from '../lib/supabase';

const MemberAuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL || '';

const isDevEnvironment = () => {
  const host = window.location.hostname;
  return host.includes('.ts.net') ||
         host === 'localhost' ||
         host === '127.0.0.1';
};

export function MemberAuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingEmail, setPendingEmail] = useState(null);

  useEffect(() => {
    const token = sessionStorage.getItem('memberToken');
    const memberData = sessionStorage.getItem('memberData');
    if (token && memberData) {
      setIsAuthenticated(true);
      try {
        setMember(JSON.parse(memberData));
      } catch (e) {
        console.error('Failed to parse member data:', e);
      }
    }
    setLoading(false);
  }, []);

  const devVerifyCode = async (email, code) => {
    if (code !== '123456') {
      return { success: false, error: 'Invalid verification code' };
    }

    const { member } = await apiFetch(
      `/api/member/by-email?email=${encodeURIComponent(email.trim())}`
    );
    let memberData = member
      ? { id: member.id, first_name: member.first_name, name: member.name, email: member.email }
      : null;

    if (!memberData) {
      const DEV_ADMIN_EMAILS = ['leo.poll@poolle.co.uk'];

      let isAdmin = false;
      try {
        const r = await apiFetch(`/api/admin/is-admin?email=${encodeURIComponent(email.trim())}`);
        isAdmin = !!r.isAdmin;
      } catch (e) {
        isAdmin = DEV_ADMIN_EMAILS.some(adminEmail =>
          adminEmail.toLowerCase() === email.trim().toLowerCase()
        );
      }

      if (!isAdmin) {
        isAdmin = DEV_ADMIN_EMAILS.some(adminEmail =>
          adminEmail.toLowerCase() === email.trim().toLowerCase()
        );
      }

      if (!isAdmin) {
        return { success: false, error: 'Invalid verification code' };
      }

      memberData = { email: email.trim(), first_name: 'Administrator' };
    }

    const sessionToken = `dev:${email}:${Date.now()}`;
    
    sessionStorage.setItem('memberToken', sessionToken);
    sessionStorage.setItem('memberData', JSON.stringify(memberData));
    setIsAuthenticated(true);
    setMember(memberData);

    return { success: true, member: memberData };
  };

  const requestCode = async (email) => {
    setPendingEmail(email);
    
    // TEMPORARILY DISABLED FOR TESTING - uncomment to restore dev mode bypass
    // if (isDevEnvironment()) {
    //   return { success: true, message: 'Dev mode: Enter code 123456 to log in' };
    // }

    try {
      const response = await fetch(`${API_BASE}/api/member/request-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, message: data.error || 'Failed to send code' };
      }
      return { success: true, message: data.message || 'Code sent to your email' };
    } catch (error) {
      console.error('Request code error:', error);
      return { success: false, message: 'Failed to send code. Please try again.' };
    }
  };

  const verifyCode = async (email, code) => {
    // TEMPORARILY DISABLED FOR TESTING - uncomment to restore dev mode bypass
    // if (isDevEnvironment()) {
    //   return devVerifyCode(email, code);
    // }

    try {
      const response = await fetch(`${API_BASE}/api/member/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      const data = await response.json();
      
      if (data.success && data.token) {
        if (data.isAdmin) {
          // Store admin session and redirect to admin page
          sessionStorage.setItem('adminToken', data.token);
          sessionStorage.setItem('adminEmail', email);
          return { ...data, redirectTo: '/admin' };
        }
        sessionStorage.setItem('memberToken', data.token);
        sessionStorage.setItem('memberData', JSON.stringify(data.member));
        setIsAuthenticated(true);
        setMember(data.member);
      }
      
      return data;
    } catch (error) {
      console.error('Verify code error:', error);
      return { success: false, error: 'Failed to verify code' };
    }
  };

  const setAuthFromToken = (token, memberData) => {
    sessionStorage.setItem('memberToken', token);
    sessionStorage.setItem('memberData', JSON.stringify(memberData));
    setIsAuthenticated(true);
    setMember(memberData);
  };

  const logout = () => {
    sessionStorage.removeItem('memberToken');
    sessionStorage.removeItem('memberData');
    setIsAuthenticated(false);
    setMember(null);
    setPendingEmail(null);
  };

  const value = {
    isAuthenticated,
    member,
    loading,
    requestCode,
    verifyCode,
    setAuthFromToken,
    logout,
    isDevEnvironment: isDevEnvironment()
  };

  return (
    <MemberAuthContext.Provider value={value}>
      {children}
    </MemberAuthContext.Provider>
  );
}

export function useMemberAuth() {
  const context = useContext(MemberAuthContext);
  if (!context) {
    throw new Error('useMemberAuth must be used within a MemberAuthProvider');
  }
  return context;
}
